import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useStorePermissions from '../../../hooks/useStorePermissions';
import {
  changeMissingClosingTotalFilteredStores,
  masterDataSelector,
  reportFilterStoresSelector,
} from '../../../state';
import {
  Agreement,
  Company,
  Management,
  Region,
  RegionalManagement,
  Stores,
  Supervision,
} from '../../../types';
import { usePermissions } from '../../Permissions';
import MultiSelect, { MultiSelectProps } from '../MultiSelect';

export interface StoreMultiSelectProps
  extends Omit<MultiSelectProps<Stores>, 'options' | 'propDesc'> {
  countryCode: string;
  regionalManagements: RegionalManagement[];
  regions: Region[];
  managements: Management[];
  supervisions: Supervision[];
  agreements: Agreement[];
  companies: Company[];
  selectAll?: boolean;
}

const storesSort = (a: Stores, b: Stores) => (a.storeAcronym > b.storeAcronym ? 1 : -1);

const StoreMultiSelect = ({
  disabled,
  countryCode,
  regionalManagements,
  regions,
  managements,
  supervisions,
  agreements,
  companies,
  onChange,
  selectAll,
  ...others
}: StoreMultiSelectProps) => {
  const masterData = useSelector(masterDataSelector);
  const stores = useSelector(reportFilterStoresSelector);
  const { resourcePermissions } = usePermissions();
  const dispatch = useDispatch();
  const storesForCountry = useMemo(() => {
    const stores = masterData.loaded && masterData.data ? masterData.data.stores : [];
    return [...stores.filter(i => i.countryCode === countryCode)].sort(storesSort);
  }, [masterData, countryCode]);

  const storesForPermissions = useStorePermissions(
    resourcePermissions,
    countryCode,
    storesForCountry
  ) as Stores[];
  // eslint-disable-next-line arrow-body-style
  const storesForRegionalManagement = useMemo(() => {
    return regionalManagements.length > 0
      ? [
          ...storesForPermissions.filter(r =>
            regionalManagements.map(i => i.regionalMgmtId).includes(r.regionalMgmtId)
          ),
        ].sort(storesSort)
      : storesForPermissions;
  }, [regionalManagements, storesForPermissions]);
  // eslint-disable-next-line arrow-body-style
  const storesForRegion = useMemo(() => {
    return regions.length > 0
      ? [
          ...storesForRegionalManagement.filter(r =>
            regions.map(i => i.regionId).includes(r.regionId)
          ),
        ].sort(storesSort)
      : storesForRegionalManagement;
  }, [storesForRegionalManagement, regions]);
  // eslint-disable-next-line arrow-body-style
  const storesForManagement = useMemo(() => {
    return managements.length > 0
      ? [...storesForRegion.filter(r => managements.map(i => i.mgmtId).includes(r.mgmtId))].sort(
          storesSort
        )
      : storesForRegion;
  }, [storesForRegion, managements]);
  // eslint-disable-next-line arrow-body-style
  const storesForSupervision = useMemo(() => {
    return supervisions.length > 0
      ? [
          ...storesForManagement.filter(r =>
            supervisions.map(i => i.supervisionId).includes(r.supervisionId)
          ),
        ].sort(storesSort)
      : storesForManagement;
  }, [storesForManagement, supervisions]);
  // eslint-disable-next-line arrow-body-style
  const storesForAgreement = useMemo(() => {
    return agreements.length > 0
      ? [
          ...storesForSupervision.filter(r =>
            agreements.map(i => i.agreementId).includes(r.agreementId)
          ),
        ].sort(storesSort)
      : storesForSupervision;
  }, [storesForSupervision, agreements]);
  // eslint-disable-next-line arrow-body-style
  const storesForCompany = useMemo(() => {
    return companies.length > 0
      ? [
          ...storesForAgreement.filter(r => companies.map(i => i.companyId).includes(r.companyId)),
        ].sort(storesSort)
      : storesForAgreement;
  }, [storesForAgreement, companies]);

  const isDisabled = disabled || !countryCode;
  const prevStoresRef = useRef<string>('');

  useEffect(() => {
    const serialized = JSON.stringify(storesForCompany.map(s => s.storeAcronym));

    if (serialized !== prevStoresRef.current) {
      prevStoresRef.current = serialized;
      dispatch(changeMissingClosingTotalFilteredStores(storesForCompany));
    }
  }, [dispatch, storesForCompany]);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && storesForCompany.length === 1 && stores.length === 0) {
      initializedRef.current = true;
      onChange(storesForCompany);
    }
  }, [onChange, stores, storesForCompany]);

  return (
    <MultiSelect<Stores>
      {...others}
      options={storesForCompany}
      propDesc="storeAcronym"
      disabled={isDisabled}
      loading={masterData.loading}
      onChange={onChange}
      selectAll={selectAll}
    />
  );
};

export default StoreMultiSelect;
